/**
 * Utility for generating a flattened image from the Picsart canvas/editor.
 * Uses the SDK's generateResultImage handler to render all layers into a single image.
 */

import type { IReplayLayer } from '@picsart/web-replay-sdk';
import { isReplayImageLayer } from '@picsart/web-replay-sdk/context';
import { getContext } from '@/context';
import withTimeout from '@/utils/async/withTimeout';
import findSelectedCanvasGroup from './findSelectedCanvasGroup';

export interface CanvasImage {
  id: string;
  name: string;
  url: string;
  width?: number;
  height?: number;
}

/**
 * Check if a layer is a system/container/background layer
 */
const isSystemLayer = (layer: IReplayLayer): boolean => {
  const systemMediaTypes = [
    'CANVAS_GROUP',
    'GROUP',
    'CANVAS',
    'ROOT',
    'PROJECT_ROOT',
    'BG',
    'BACKGROUND',
    'BACKGROUND_COLOR',
  ];
  const systemTypes = [
    'canvas',
    'group',
    'root',
    'container',
    'bg',
    'background',
  ];

  if (systemMediaTypes.includes(layer.mediaType)) {
    return true;
  }
  if (systemTypes.includes(layer.type.toLowerCase())) {
    return true;
  }

  const nameLower = layer.name.toLowerCase();
  const mediaTypeLower = layer.mediaType.toLowerCase();
  if (
    nameLower === 'bg' ||
    nameLower.includes('background') ||
    nameLower.startsWith('bg_')
  )
    return true;
  if (mediaTypeLower.includes('background')) return true;

  return false;
};

// SDK layers are stored as Record<UUIDString, IReplayLayer>
type LayersRecord = Record<string, IReplayLayer>;

/**
 * Generate canvas as a single image using the SDK handler.
 * If canvas has layers, generates a rendered image.
 * If canvas is empty, returns null.
 */
export const generateCanvasImage = async (): Promise<CanvasImage | null> => {
  try {
    const context = getContext();

    // Check if canvas has any layers
    const layers = context?.scene?.replay?.context?.layers;
    if (!layers || Object.keys(layers).length === 0) {
      return null;
    }

    const selectedCanvasGroup = findSelectedCanvasGroup({
      replay: context.scene.replay,
    });

    if (!selectedCanvasGroup) {
      return null;
    }

    // Filter out system layers to check if there's any actual content
    const layersData = layers as LayersRecord;
    const layerEntries = Object.entries(layersData).filter(
      ([, layer]) => layer != null && !isSystemLayer(layer)
    );

    // If no actual content layers, return null
    if (layerEntries.length === 0) {
      return null;
    }

    // Generate the canvas image using SDK handler (with timeout to prevent hanging)
    const photoResource = await withTimeout({
      promise: context.handlers.generateResultImage(
        context.scene.selectedCanvasId,
        {
          over: true,
        }
      ),
      timeoutMs: 30000,
      fallback: null,
    });

    if (!photoResource?.location) {
      return null;
    }

    return {
      id: photoResource.id,
      name: 'Canvas',
      url: photoResource.location,
      width: photoResource.width,
      height: photoResource.height,
    };
  } catch {
    // This is fallback for mobile, because generateResultImage is not supported on mobile in some touchpoints
    const [selectedImageLayerId] = getContext().scene.selectedLayerIds;
    const selectedLayer =
      getContext().scene.replay.context.layers[selectedImageLayerId];

    if (selectedLayer && isReplayImageLayer(selectedLayer)) {
      return {
        name: 'selected image',
        url: selectedLayer.props.image.value.location,
        id: selectedLayer.uuid,
      };
    }
    return null;
  }
};
